import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as IconChevronRounded } from 'media/svg/chevronRounded.svg';

import { getDecimalNumber } from 'utility/Format/Number';

import styles from './Pagination.module.scss';

export interface PaginationProps {
    /** How many items make up one page */
    itemsPerPage: number;
    /** Where should it start, once it renders (no lifecycle!) */
    initOnPage?: number;
    /** How many items are there in total (required for calculating page amount) */
    maxItems: number;
    /** From which index shall we truncate the pageList */
    truncateThreshold?: number;
    /** Callback for changing the page (with currentPage param) */
    onPageSwitch: (page: number) => void;
}

function getPaginationItems(
    currentPage: number,
    pagesTotal: number,
    truncateThreshold: number
): (string | number)[] {
    const list: number[] = [...Array(pagesTotal + 1).keys()].slice(1);
    const lowerThreshold = Math.floor(truncateThreshold / 2);
    const upperThreshold = truncateThreshold - lowerThreshold;
    let startPage = currentPage - lowerThreshold;
    let endPage = currentPage + upperThreshold;

    /** less pages in total as truncateThreshold amount */
    if (pagesTotal <= truncateThreshold) {
        return list;
    }

    /** Case [1 2 3 4 5 6 ... 48] */
    if (startPage <= 1) {
        endPage -= startPage - 1;
        startPage = 1;
    }

    /** Case [1 ... 43 44 45 46 47 48 ] */
    if (endPage > pagesTotal - 1) {
        startPage -= endPage - pagesTotal;
        endPage = pagesTotal;
    }

    /** Case [1 2 3 4 5 6 7 ... 48] */
    if (startPage <= 3) {
        if (endPage >= pagesTotal - 2) {
            return list;
        }

        return [...list.slice(0, endPage), '...', pagesTotal];
    }

    /** Case [1 ... 42 43 44 45 46 47 48 ] */
    if (endPage >= pagesTotal - 2) {
        return [1, '...', ...list.slice(startPage - 1, pagesTotal)];
    }
    if (pagesTotal <= truncateThreshold + 4) {
        return list;
    }

    /** Default Case [1 ... 20 21 22 23 24 25 26 ... 48 ] */
    return [1, '...', ...list.slice(startPage - 1, endPage), '...', pagesTotal];
}

const Pagination: React.FC<PaginationProps> = ({
    itemsPerPage,
    initOnPage = 1,
    maxItems,
    truncateThreshold = 5,
    onPageSwitch,
}) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(initOnPage);

    /** Calculate pages from maxItems and ItemsPerPage - ceil it to prevent .5 values */
    const pagesTotal = Math.ceil(maxItems / itemsPerPage);

    /** Handling for switching the page */
    function handlePageSwitch(index: number) {
        setCurrentPage(index);
        onPageSwitch(index);
    }

    return (
        <div className={styles.Pagination}>
            <div className={styles.Label}>
                {t('TRANS-results-x-of-y', {
                    currentResults: `${getDecimalNumber(
                        (currentPage - 1) * itemsPerPage + 1,
                        0
                    )} - ${
                        maxItems < currentPage * itemsPerPage
                            ? getDecimalNumber(maxItems, 0)
                            : getDecimalNumber(currentPage * itemsPerPage, 0)
                    }`,
                    maxResults: getDecimalNumber(maxItems, 0),
                })}
            </div>
            <div className={styles.Pages}>
                <span
                    onClick={() => handlePageSwitch(currentPage - 1)}
                    onKeyDown={() => {}}
                    tabIndex={0}
                    role="link"
                >
                    {currentPage > 1 && <IconChevronRounded />}
                </span>

                {getPaginationItems(
                    currentPage,
                    pagesTotal,
                    truncateThreshold
                ).map((key: number | string, index: number) => {
                    if (typeof key === 'number') {
                        index = index + 1;
                        return (
                            <span
                                onClick={() => handlePageSwitch(key)}
                                onKeyDown={() => {}}
                                tabIndex={0}
                                role="link"
                                key={index}
                                className={
                                    key === currentPage ? styles.Active : ''
                                }
                            >
                                {key}
                            </span>
                        );
                    } else {
                        return (
                            <span className={styles.Truncated} key={index + 1}>
                                {key}
                            </span>
                        );
                    }
                })}
                <span
                    onClick={() => handlePageSwitch(currentPage + 1)}
                    onKeyDown={() => {}}
                    tabIndex={0}
                    role="link"
                >
                    {currentPage < pagesTotal && <IconChevronRounded />}
                </span>
            </div>
        </div>
    );
};

export default Pagination;
